import { trackEvent } from '@pm/analytics';
import { ButtonLink, Icon, IconTile, Text } from '@pm/ui';
import { useTranslation } from 'react-i18next';
import { useEnvironment } from '../../../hooks/env';
import { labsRoutes } from '../../../modules/labs/labs-routes';
import { SupportedLanguage } from '../../../types';
import { formatDateLongMonthDayYear } from '../../../utilities/dateUtilities';
import { useMarkRequisitionViewed } from '../useMarkRequisitionViewed';
import { toLabRequisitionUrl } from '../utils';
import { HeroCardAction } from './HeroCardAction';
// import { ClearResultsMessage, DetectedResultsMessage } from './ResultMessages';
import { TrackingSection } from './TrackingSection';
import { LabHeroCardProps } from './types';

export type { LabHeroCardProps };

// Per-kind visual config — keeps inline styles out of the render body.
// Kinds omitted here (e.g. lab-result-document) render with no background image.
const CARD_VISUALS: Partial<
  Record<
    Exclude<LabHeroCardProps['kind'], 'empty'>,
    { backgroundImage: string; backgroundPosition: string }
  >
> = {
  'lab-kit': {
    backgroundImage: 'url(/images/lab-kit-hero.png)',
    backgroundPosition: 'right top',
  },
  'lab-requisition': {
    backgroundImage: 'url(/images/lab-requisition-hero.png)',
    backgroundPosition: 'right center',
  },
};

const StatusContent = (props: Exclude<LabHeroCardProps, { kind: 'empty' }>) => {
  const { t } = useTranslation('labs');
  const { apiHost } = useEnvironment();

  // Opening the requisition must record the view, otherwise the "viewed" status
  // never updates. Only the needs-download requisition card carries a requisition.
  const requisitionToMark =
    props.kind === 'lab-requisition' && props.status.type === 'needs-download'
      ? {
          id: props.status.requisitionId,
          readByPatientAt: props.status.readByPatientAt,
        }
      : { id: '', readByPatientAt: null };
  const { markAsViewed } = useMarkRequisitionViewed(requisitionToMark);

  if (props.kind === 'lab-kit' && props.status.type === 'tracking') {
    return (
      <TrackingSection
        step={props.status.step}
        outboundShipment={props.status.outboundShipment}
        inboundShipment={props.status.inboundShipment}
        supportLink={props.status.supportLink}
      />
    );
  }

  if (props.status.type === 'ready') {
    return (
      <div className="pt-xl">
        {/* {(props.status.detectedTestNames ?? []).length > 0 ? <DetectedResultsMessage /> : <ClearResultsMessage />} */}
        <HeroCardAction
          label={t('HeroCard.ViewResults')}
          icon="arrow_forward"
          to={labsRoutes.result.build({ id: props.status.resultId })}
        />
      </div>
    );
  }

  if (
    props.kind === 'lab-requisition' &&
    props.status.type === 'needs-download'
  ) {
    const { feedbackStepUrl } = props.status;
    const downloadUrl = toLabRequisitionUrl(props.status.downloadUrl, apiHost);
    return (
      <div className="pt-xl flex flex-col items-start gap-s w-fit max-w-full">
        <ButtonLink
          to={downloadUrl}
          intent="secondary"
          size="small"
          external
          onClick={() => markAsViewed()}
        >
          <span className="inline-flex items-center gap-s">
            {t('HeroCard.Download')}
            <Icon name="download" size="medium" />
          </span>
        </ButtonLink>
        {feedbackStepUrl && (
          <ButtonLink
            to={feedbackStepUrl}
            intent="secondary"
            size="small"
            onClick={() =>
              trackEvent('Button Clicked', {
                buttonText: t('FeedbackButton'),
                buttonName: 'lab_feedback_button',
                currentPageName: 'Lab Work',
                currentPath: window.location.pathname,
                targetPath: feedbackStepUrl,
              })
            }
          >
            {t('FeedbackButton')}
          </ButtonLink>
        )}
      </div>
    );
  }

  if (props.kind === 'lab-result-document') {
    return (
      <div className="pt-xl">
        <HeroCardAction
          label={t('HeroCard.Download')}
          icon="download"
          to={labsRoutes.document.build({ id: props.status.resultId })}
        />
      </div>
    );
  }

  return null;
};

// Renders the card content (title, date, status) without any framing.
// Use this when the parent already provides a border/background.
// Use LabHeroCard when you need the standalone framed card (e.g. LabsPage).
export const LabHeroCardContent = (
  props: Exclude<LabHeroCardProps, { kind: 'empty' }>,
) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('labs');

  const formattedDate = formatDateLongMonthDayYear(
    props.date,
    language as SupportedLanguage,
  );
  const TITLE_KEYS: Record<
    Exclude<LabHeroCardProps['kind'], 'empty'>,
    string
  > = {
    'lab-kit': t('HeroCard.AtHomeLabKit'),
    'lab-result-document': t('HeroCard.LabResult'),
    'lab-requisition': t('HeroCard.LabRequisition'),
  };
  const title = TITLE_KEYS[props.kind];
  const visuals = CARD_VISUALS[props.kind];

  return (
    // -m-m bleeds back out to the parent's p-m padding edge so that background-size/position
    // resolve against the full card dimensions rather than the inset content box.
    // Consumers must clip overflow (overflow-hidden on the framing wrapper).
    <div
      className="-m-m p-m relative h-full bg-no-repeat bg-[length:30%_auto]"
      style={visuals ?? {}}
    >
      <div className="max-w-[68%] space-y-s">
        <Text size="headingS" as="h2" className="whitespace-nowrap">
          {title}
        </Text>
        <Text size="bodyS" color="contentSubdued">
          {formattedDate}
        </Text>
      </div>

      <StatusContent {...props} />
    </div>
  );
};

export const LabHeroCard = (props: LabHeroCardProps) => {
  const { t } = useTranslation('labs');

  if (props.kind === 'empty') {
    return (
      <div className="rounded-m border bg-surface p-m flex flex-col items-center justify-center text-center space-y-s">
        <IconTile icon="labs" size="large" intent="info" />
        <Text size="bodyL">{t('HeroCard.NoActiveLabWork')}</Text>
      </div>
    );
  }

  return (
    <div className="rounded-m border bg-surface-strong p-m overflow-hidden">
      <LabHeroCardContent {...props} />
    </div>
  );
};
